/**
 * Booking form.
 *
 * The one interaction the whole deployment depends on. Two failure modes are
 * distinguished deliberately: a slot lost to a colleague is not an error the
 * user caused, so it is explained and the form closes rather than inviting a
 * pointless retry.
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { bookerEmail, formatDayFr, type PublicSlot } from "@soi/contracts";
import { ApiFailure, api } from "../lib/api.js";
import { queryKeys } from "../lib/queries.js";
import { useToast } from "./Toast.js";

interface Target {
  slot: PublicSlot;
  day: string;
  siteName: string;
  siteSlug: string;
  intervenantName: string;
}

export function BookingDialog({ target, onClose }: { target: Target; onClose: () => void }) {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    contactPhone: "",
    floor: "",
    notes: "",
  });
  const [status, setStatus] = useState<{ kind: "err" | "ok"; message: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const queryClient = useQueryClient();
  const toast = useToast();

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      setStatus({
        kind: "err",
        message: "Merci de renseigner votre prénom, votre nom et votre e-mail.",
      });
      return;
    }
    const email = bookerEmail.safeParse(form.email);
    if (!email.success) {
      setStatus({ kind: "err", message: email.error.issues[0]!.message });
      return;
    }

    setBusy(true);
    setStatus(null);
    try {
      const reservation = await api.book({ slotId: target.slot.id, ...form });
      setStatus({
        kind: "ok",
        message: `Rendez-vous confirmé. Une invitation vous parvient à ${reservation.email}.`,
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.site(target.siteSlug) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.sites });
      setTimeout(onClose, 2200);
    } catch (err) {
      if (err instanceof ApiFailure && err.code === "CRENEAU_DEJA_RESERVE") {
        // Someone else won the race. Refresh so the grid shows the truth, and
        // send the user back to it rather than leaving them on a dead form.
        void queryClient.invalidateQueries({ queryKey: queryKeys.site(target.siteSlug) });
        toast(err.message, "err");
        onClose();
        return;
      }
      setStatus({
        kind: "err",
        message:
          err instanceof ApiFailure
            ? err.message
            : "La réservation n'a pas pu être enregistrée. Réessayez ou contactez windows11@lausanne.ch.",
      });
      setBusy(false);
    }
  };

  return (
    <div className="ovl" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <div className="modal-head">
          <button type="button" className="modal-close" aria-label="Fermer" onClick={onClose}>
            ✕
          </button>
          <h2 id="modal-title">Réserver ce créneau</h2>
          <p>
            {target.siteName} · {formatDayFr(target.day)} à {target.slot.time} avec{" "}
            {target.intervenantName}
          </p>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="field-row">
              <div className="field">
                <label htmlFor="f-prenom">
                  Prénom <span className="req">*</span>
                </label>
                <input
                  type="text"
                  id="f-prenom"
                  autoComplete="given-name"
                  value={form.firstName}
                  onChange={set("firstName")}
                />
              </div>
              <div className="field">
                <label htmlFor="f-nom">
                  Nom <span className="req">*</span>
                </label>
                <input
                  type="text"
                  id="f-nom"
                  autoComplete="family-name"
                  value={form.lastName}
                  onChange={set("lastName")}
                />
              </div>
            </div>
            <div className="field">
              <label htmlFor="f-email">
                Adresse e-mail <span className="req">*</span>
              </label>
              <input
                type="email"
                id="f-email"
                autoComplete="email"
                placeholder="prenom.nom@lausanne.ch"
                value={form.email}
                onChange={set("email")}
              />
            </div>
            <div className="field">
              <label htmlFor="f-telephone">
                Numéro de contact <span className="opt">(facultatif)</span>
              </label>
              <input
                type="tel"
                id="f-telephone"
                autoComplete="tel"
                placeholder="ex. 021 315 00 00"
                value={form.contactPhone}
                onChange={set("contactPhone")}
              />
            </div>
            <div className="field">
              <label htmlFor="f-etage">
                Étage / bureau <span className="opt">(facultatif)</span>
              </label>
              <input
                type="text"
                id="f-etage"
                placeholder="ex. 3e étage, bureau 312"
                value={form.floor}
                onChange={set("floor")}
              />
            </div>
            <div className="field">
              <label htmlFor="f-remarques">
                Remarques <span className="opt">(facultatif)</span>
              </label>
              <textarea
                id="f-remarques"
                placeholder="Périphérique particulier, accès, contrainte horaire…"
                value={form.notes}
                onChange={set("notes")}
              />
            </div>
          </div>
          {status && (
            <p className={`modal-status ${status.kind}`} role={status.kind === "err" ? "alert" : "status"}>
              {status.message}
            </p>
          )}
          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Annuler
            </button>
            <button type="submit" className="btn-submit" disabled={busy} data-testid="confirm-booking">
              {busy ? "Envoi en cours…" : "Confirmer la réservation"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
